/**
 * Mutation queue — durable FIFO of pending writes.
 *
 * Every write to a Dexie repo also enqueues an entry here. The sync engine
 * (services/sync/push.js) drains entries when the user is authed AND online.
 *
 * Local-ID problem: anon sessions get a uuid as their id. On first create
 * push the server returns either the same id (we accept client-supplied IDs)
 * or a new one. If it differs, we `rewriteRecordId` so subsequent update/delete
 * entries reference the right id.
 *
 * Entry shape:
 *   {
 *     id?: number,         // Dexie ++id
 *     entity: 'session',
 *     op: 'create' | 'update' | 'delete',
 *     recordId: string,    // session id at enqueue time
 *     payload: object,
 *     status: 'pending' | 'in_flight' | 'failed' | 'dead',
 *     attempts: number,
 *     createdAt: string,   // ISO
 *     lastError?: string,
 *   }
 */

import { db } from './index';

const MAX_ATTEMPTS = 6;

export async function enqueue({ entity, op, recordId, payload }) {
  await db.mutationQueue.add({
    entity,
    op,
    recordId,
    payload,
    status: 'pending',
    attempts: 0,
    createdAt: new Date().toISOString(),
  });
}

export async function listPending() {
  return db.mutationQueue
    .where('status')
    .anyOf('pending', 'failed')
    .toArray();
}

export async function markInFlight(id) {
  await db.mutationQueue.update(id, { status: 'in_flight' });
}

export async function markCompleted(id) {
  await db.mutationQueue.delete(id);
}

export async function markFailed(id, error) {
  const entry = await db.mutationQueue.get(id);
  if (!entry) return;
  const attempts = (entry.attempts || 0) + 1;
  const status = attempts >= MAX_ATTEMPTS ? 'dead' : 'failed';
  await db.mutationQueue.update(id, {
    status,
    attempts,
    lastError: error?.message || String(error),
  });
}

/**
 * Rewrite recordId on every queue entry that references oldId. Called after
 * a create succeeds and the server returns a different id.
 */
export async function rewriteRecordId(oldId, newId) {
  const matches = await db.mutationQueue.where('recordId').equals(oldId).toArray();
  for (const entry of matches) {
    await db.mutationQueue.update(entry.id, { recordId: newId });
  }
  return matches.length;
}

export async function getStatus() {
  const counts = { pending: 0, in_flight: 0, failed: 0, dead: 0 };
  const rows = await db.mutationQueue.toArray();
  for (const row of rows) {
    counts[row.status] = (counts[row.status] || 0) + 1;
  }
  counts.total = rows.length;
  return counts;
}

export async function clearAll() {
  await db.mutationQueue.clear();
}
